import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  LINE_ITEM_OFFER,
  LINE_ITEM_REQUEST,
  propTypes,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

/**
 * A component that renders the base price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {propTypes.lineItemUnitType} props.code - The code of the line item
 * @param {Array<string|null|undefined>} [props.itemLabels] - Listing titles, index-aligned with item rows
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemBasePriceMaybe = props => {
  const { lineItems, code, intl, itemLabels } = props;
  const isNightly = code === LINE_ITEM_NIGHT;
  const isDaily = code === LINE_ITEM_DAY;
  const isHourly = code === LINE_ITEM_HOUR;
  const isFixed = code === LINE_ITEM_FIXED;
  const isRequest = code === LINE_ITEM_REQUEST;
  const isOffer = code === LINE_ITEM_OFFER;
  const translationKey = isNightly
    ? 'OrderBreakdown.baseUnitNight'
    : isDaily
    ? 'OrderBreakdown.baseUnitDay'
    : isHourly
    ? 'OrderBreakdown.baseUnitHour'
    : isFixed
    ? 'OrderBreakdown.baseUnitFixedBooking'
    : isRequest
    ? 'OrderBreakdown.baseUnitRequest'
    : isOffer
    ? 'OrderBreakdown.baseUnitOffer'
    : 'OrderBreakdown.baseUnitQuantity';

  // One row per matching line-item. A cart box emits one line-item/item per listing.
  const unitPurchases = lineItems.filter(item => item.code === code && !item.reversal);

  const rows = unitPurchases
    .map((unitPurchase, i) => {
      const quantity = unitPurchase?.units
        ? unitPurchase.units.toString()
        : unitPurchase?.quantity
        ? unitPurchase.quantity.toString()
        : null;
      const unitPrice = unitPurchase ? formatMoney(intl, unitPurchase.unitPrice) : null;
      const total = unitPurchase ? formatMoney(intl, unitPurchase.lineTotal) : null;
      const title = itemLabels?.[i];

      const quantityMessage = unitPurchase?.seats ? (
        <FormattedMessage
          id={`${translationKey}Seats`}
          values={{ unitPrice, quantity, seats: unitPurchase.seats }}
        />
      ) : (
        <FormattedMessage id={translationKey} values={{ unitPrice, quantity }} />
      );

      const message = title ? (
        <>
          <span className={css.itemName}>{title}</span>
          {' · '}
          {unitPrice} x {quantity}
        </>
      ) : (
        quantityMessage
      );

      return quantity && total ? (
        <div key={`${code}-${i}`} className={css.lineItem}>
          <span className={css.itemLabel}>{message}</span>
          <span className={css.itemValue}>{total}</span>
        </div>
      ) : null;
    })
    .filter(Boolean);

  return rows.length > 0 ? <>{rows}</> : null;
};

export default LineItemBasePriceMaybe;
